import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import { processImagesWithOverlay } from "../../../utils/imageOverlay";
import ImageSourceDropdown from "../../customer-survey/components/ImageSourceDropdown";

const PhotoUploadCard = ({
  title,
  description,
  photoUrl,
  onUpload,
  onRemove,
  required = false,
  uploadFunction,
  overlayMeta = null,
  capturedLocation = null,
  readOnly = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (selected) => {
    const file = selected instanceof File ? selected : selected?.target?.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      // Process image with overlay (timestamp and location)
      const processedFiles = await processImagesWithOverlay(
        [file],
        overlayMeta,
        capturedLocation,
      );
      const processedFile = processedFiles[0] || file; // Fallback to original if processing fails

      if (uploadFunction) {
        // Use provided upload function (uploads to Supabase)
        const url = await uploadFunction(processedFile);
        if (url != null && url !== "") {
          onUpload(url);
        }
      } else {
        // Fallback to blob URL (temporary, for development)
        const mockUrl = URL.createObjectURL(processedFile);
        onUpload(mockUrl);
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      alert("Failed to upload photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="text-sm md:text-base font-medium text-foreground flex items-center gap-2">
            {title}
            {required && <span className="text-error text-xs">*</span>}
          </h4>
          {description && (
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {description}
            </p>
          )}
        </div>
      </div>

      {photoUrl ? (
        <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted">
          <Image
            src={photoUrl}
            alt={`${title} - Installation documentation photo showing completed work`}
            className="w-full h-full object-contain bg-muted"
          />
          <div className="absolute top-2 right-2 flex gap-2">
            {!readOnly && (
              <Button
                variant="destructive"
                size="icon"
                onClick={onRemove}
                iconName="Trash2"
                className="w-8 h-8"
              />
            )}
          </div>
          <div className="absolute bottom-2 left-2 bg-success text-success-foreground px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
            <Icon name="Check" size={12} />
            Uploaded
          </div>
        </div>
      ) : readOnly ? (
        <div className="aspect-[4/3] w-full rounded-lg border border-border bg-muted/40 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">No photo uploaded</p>
        </div>
      ) : (
        <div className="aspect-[4/3] w-full">
          <ImageSourceDropdown
            idPrefix={`installation-photo-${title?.toLowerCase?.()?.replace(/\s+/g, "-") || "upload"}`}
            triggerLabel={isUploading ? "Uploading..." : "Add Photo"}
            triggerSubLabel="Choose from album or capture live photo"
            processing={isUploading}
            disabled={isUploading}
            onFileSelect={handleFileChange}
            triggerClassName="h-full aspect-[4/3] w-full rounded-lg border-2 border-dashed border-border hover:border-primary bg-muted/50 hover:bg-muted cursor-pointer transition-all duration-250 flex flex-col items-center justify-center gap-2 py-4"
          />
        </div>
      )}
    </div>
  );
};

export default PhotoUploadCard;
